import React, { useState, useEffect } from "react";
import {
  Select,
  MenuItem,
  Typography,
  Box,
  TextField,
  Grid,
  Paper,
  FormControl,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import { Map as MapIcon } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { Controller } from "react-hook-form";
import * as yup from "yup";
import { getAllEmirates } from "../../api/emirates";
import { getRoutesByEmirates } from "../../api/routes";

const CancelRequest = ({ control, errors, setValue }) => {
  const [emirates, setEmirates] = useState([]);
  const [routes, setRoutes] = useState([]);

  const fetchEmirates = async () => {
    try {
      const data = await getAllEmirates();
      setEmirates(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error("Error fetching emirates:", error);
    }
  };

  const fetchRoutesByEmirates = async (emirateId) => {
    if (setValue) {
      setValue("route", "");
    }
    setRoutes([]);
    if (!emirateId) return;

    try {
      const data = await getRoutesByEmirates(emirateId);
      if (data && data.status && Array.isArray(data.data)) {
        setRoutes(data.data);
      } else if (Array.isArray(data)) {
        setRoutes(data);
      } else {
        setRoutes([]);
      }
    } catch (error) {
      console.error("Error fetching routes:", error);
      setRoutes([]);
    }
  };

  useEffect(() => {
    fetchEmirates();
    if (setValue) {
      setValue("noticeDate", moment());
      setValue("cancellationDate", moment().add(90, "days"));
    }
  }, [setValue]);

  return (
    <Box sx={{ width: "75%", margin: "auto", mt: 3 }}>
      <Paper elevation={0} sx={{ border: "1px solid black", p: 2 }}>
        <Box display="flex" alignItems="center" sx={{ bgcolor: "#e8e8e8", p: 1, mb: 2 }}>
          <Typography variant="subtitle1" component="div" sx={{ fontWeight: "bold" }}>
            Please fill the details for transport cancellation: (TRANSPORT CANCELLATION REQUEST)
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Emirates
            </Typography>
            <Controller
              name="emirate"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth variant="standard">
                  <Select
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      fetchRoutesByEmirates(e.target.value);
                    }}
                    displayEmpty
                    error={!!errors.emirate}
                    inputProps={{ "aria-label": "Select emirate" }}
                    sx={{
                      "&:before": { borderBottom: "1px solid black" },
                      "&:after": { borderBottom: "1px solid black" },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select emirate
                    </MenuItem>
                    {emirates?.map((emirate) => (
                      <MenuItem key={emirate?.eid} value={emirate?.eid}>
                        {emirate?.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.emirate && (
                    <Typography color="error" variant="caption">
                      {errors.emirate.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Route
            </Typography>
            <Controller
              name="route"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={routes}
                  getOptionLabel={(option) =>
                    typeof option === "object" && option !== null
                      ? `${option.routename || ""} - ${option.routeno || ""}`
                      : ""
                  }
                  value={routes.find((r) => r.rid === field.value) || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue ? newValue.rid : "");
                  }}
                  isOptionEqualToValue={(option, value) => option.rid === value.rid}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      key={option.rid}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "0.9rem",
                      }}
                    >
                      <span>{`${option.routename} - ${option.routeno}`}</span>
                      {option.map && (
                        <Tooltip title="Show route on map" arrow>
                          <MapIcon
                            sx={{
                              marginLeft: "auto",
                              cursor: "pointer",
                              fontSize: "1.2rem",
                              color: "primary.main",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(option.map, "_blank");
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      placeholder="Search and select route..."
                      error={!!errors.route}
                      helperText={errors.route?.message}
                      sx={{
                        "& .MuiInput-root": {
                          borderBottom: "1px solid black",
                          "&:before, &:after": { display: "none" },
                        },
                      }}
                    />
                  )}
                  ListboxProps={{
                    sx: { maxHeight: 250, overflowY: "auto" },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Notice Date
            </Typography>
            <Controller
              name="noticeDate"
              control={control}
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    {...field}
                    disabled={true}
                    slotProps={{
                      textField: {
                        variant: "standard",
                        size: "small",
                        fullWidth: true,
                        error: !!errors.noticeDate,
                        helperText: errors.noticeDate?.message,
                        sx: {
                          cursor: "not-allowed",
                          "& .MuiInput-root": {
                            cursor: "not-allowed",
                            borderBottom: "1px solid black",
                            "&:before, &:after": { display: "none" },
                          },
                          "& input": {
                            cursor: "not-allowed",
                            fontSize: { xs: "0.6rem", md: "0.8rem", xl: "1.2rem" },
                            textAlign: "center",
                            marginTop: "-5px",
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Cancellation with effect from
            </Typography>
            <Controller
              name="cancellationDate"
              control={control}
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    {...field}
                    disabled={true}
                    slotProps={{
                      textField: {
                        variant: "standard",
                        size: "small",
                        fullWidth: true,
                        error: !!errors.cancellationDate,
                        helperText: errors.cancellationDate?.message,
                        sx: {
                          cursor: "not-allowed",
                          "& .MuiInput-root": {
                            cursor: "not-allowed",
                            borderBottom: "1px solid black",
                            "&:before, &:after": { display: "none" },
                          },
                          "& input": {
                            cursor: "not-allowed",
                            fontSize: { xs: "0.6rem", md: "0.8rem", xl: "1.2rem" },
                            textAlign: "center",
                            marginTop: "-5px",
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" component="label">
              Reason for cancellation
            </Typography>
            <Controller
              name="cancellationReason"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  error={!!errors.cancellationReason}
                  helperText={errors.cancellationReason?.message}
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    "& .MuiInput-root": {
                      borderBottom: "1px solid black",
                      "&:before, &:after": { display: "none" },
                    },
                  }}
                />
              )}
            />
          </Grid>
        </Grid>

        <Typography variant="caption" sx={{ mt: 2, display: "block", fontSize: "0.7rem" }}>
          Note:- Request for discontinuation of transport facility must be submitted THREE MONTHS in
          advance to the Al Saraya office. If the bus usages are discontinued without written
          notice, then it will be deemed that the student continues to use the bus services,
          irrespective of actual usage. This also applies to the fee defaulter of that particular
          term.
        </Typography>
      </Paper>
    </Box>
  );
};

CancelRequest.validationSchema = yup.object().shape({
  emirate: yup.string().required("Emirates is required"),
  route: yup.string().required("Route is required"),
  noticeDate: yup.date().required("Notice Date is required"),
  cancellationDate: yup.date().required("Cancellation Date is required"),
  cancellationReason: yup.string().required("Reason for cancellation is required"),
});

export default CancelRequest;
